import { fetchCollageLayouts, fetchLayoutSVGText } from './layoutFetcher'
import {
  COLLAGE_LAYOUT_STYLES,
  TILESIZES_TO_INCHES,
} from '@mixtiles/web-backend-shared'
import {
  getCollageLayoutContainer,
  getCollageTextContainer,
  parseCollagePhotoPositions,
  parseLayoutSVG,
} from './collageParser'
import { DEFAULT_IMAGE_DPI } from '../../utils/ImageUtils'
import { COLLAGE_FONT_COLOR, COLLAGE_FONTS, getFontSize } from './collageText'
import {
  getHtmlElementAttributes,
  roundFloat,
  svgToString,
} from '../../utils/utils'
import { findPositionMatching } from './collagePositionMatcher'
import { logger } from '../logger'
import { calculateCropForRatio } from '../../utils/cropUtils'
import { fitCropToAllRatios } from '../../utils/cropRatioUtils'
import { Semaphore } from 'async-mutex'
import { COLLAGE_SVG_COMPONENTS } from '../../components/CollageTile/CollageSvg/CollageSvg.consts'

const PHOTO_ASPECT_RATIO_PRECISION = 3 // How much should a crop aspect ratio be rounded to
const EMPTY_COLLAGE_TEXT = { showText: false, text: '', textStyleId: null }

export async function loadCollageSvg(layout, isTextLayout) {
  const { svgContent, textSvgContent } = await fetchLayoutSVGText(layout)
  const isText = isTextLayout && textSvgContent
  return parseLayoutSVG({
    svgContent: isText ? textSvgContent : svgContent,
    isText,
  })
}

export async function getDefaultLayout({ photosCount, tile }) {
  const layouts = await fetchCollageLayouts(photosCount, tile)
  if (!layouts) {
    return null
  }
  const layoutStyles = Object.keys(layouts)
  return layouts[layoutStyles[0]]
}

export async function createCollage({ layout, collagePhotos }) {
  return initCollage({
    layout,
    collagePhotos,
    collageText: EMPTY_COLLAGE_TEXT,
  })
}

export function getPhotoAspectRatio(position) {
  return roundFloat(
    position.width / position.height,
    PHOTO_ASPECT_RATIO_PRECISION
  )
}

/**
 * We save cropping params for each aspect ratio of the photo (because the same photo might
 * have different aspect ratios - for example when changing layout, changing tile size,
 * swapping photo position etc). Therefore, we need to return the cropping params that
 * match the current photo aspect ratio
 */
export function getPhotoCroppingParams(photo) {
  const { position, cropParams = {}, cropperData = {} } = photo
  const aspectRatio = getPhotoAspectRatio(position)
  return {
    cropParams: cropParams[aspectRatio] || {},
    cropperData: cropperData[aspectRatio] || {},
  }
}

/**
 * Override crop params for current aspect ratio, and keep the rest of the crop params
 */
export function updatePhotoCroppingParams(photo, updatedCropParams) {
  const { position, cropParams, cropperData, width, height } = photo
  const aspectRatio = getPhotoAspectRatio(position)
  const adjustCropParams = fitCropToAllRatios({
    ratiosToCalculate: Object.keys(cropParams || {}),
    cropRect: updatedCropParams.cropParams,
    currentAspectRatio: aspectRatio,
    zoom: updatedCropParams.cropperData?.zoom,
    imageWidth: width,
    imageHeight: height,
  })

  return {
    cropParams: adjustCropParams,
    cropperData: {
      ...cropperData,
      [aspectRatio]: updatedCropParams.cropperData,
    },
  }
}

/**
 * Receive a list of collage photos and an object of all collage positions and match them together
 * @param collagePhotos - a list of collage photos
 * @param photoPositions - an object of photoId to position attributes
 * @param layout - the collage layout
 * @return an object of photoId to the correct photo based on determined position
 */
async function matchCollagePhotosToPositions({
  collagePhotos,
  photoPositions,
  layout,
}) {
  const matching = findPositionMatching({ collagePhotos, photoPositions })
  if (layout.style === COLLAGE_LAYOUT_STYLES.CENTERED) {
    // In centered layouts, we never want the highlighted photo to be empty
    if (matching[0] === null) {
      const positionWithPhoto = Object.keys(matching).find(
        (positionId) => !!matching[positionId]
      )
      matching[0] = matching[positionWithPhoto]
      matching[positionWithPhoto] = null
    }
  }
  return Object.fromEntries(
    await Promise.all(
      Object.entries(matching).map(async ([positionId, photo]) => {
        const matchedPositionId = Object.keys(photoPositions)[positionId]
        return [
          matchedPositionId,
          await putPhotoInPosition({
            photo,
            position: photoPositions[matchedPositionId],
          }),
        ]
      })
    )
  )
}

// Limit the number of concurrent photo crop calculations to prevent high memory usage by smart crop
const photoCropSemaphore = new Semaphore(3)

function limitedCalculateCropForRatio({ targetRatio, photo }) {
  return photoCropSemaphore.runExclusive(async () => {
    return calculateCropForRatio({
      targetRatio,
      photo,
    })
  })
}

async function putPhotoInPosition({ photo, position }) {
  const { uid, cropParams = {}, cropperData = {} } = photo || {}
  const editableCropParams = { ...cropParams }
  const targetRatio = getPhotoAspectRatio(position)
  if (uid && !cropParams[targetRatio]) {
    editableCropParams[targetRatio] = await limitedCalculateCropForRatio({
      targetRatio,
      photo,
    })
  }

  return {
    uid,
    position,
    cropParams: editableCropParams,
    cropperData: {
      // Keep old crop params if they exist for photo
      [targetRatio]: {},
      ...cropperData,
    },
    customStyle: {},
  }
}

/**
 * Initialize a collage object
 * @param layout - the collage layout
 * @param collagePhotos - a list of photo bank photos to assign to collage
 * @param collageText - the state of text on collage. Structure: { showText, text, textStyleId }
 * @return collage structure
 */
export async function initCollage({ layout, collagePhotos, collageText }) {
  try {
    const initialCollageText = collageText || EMPTY_COLLAGE_TEXT
    const { showText } = initialCollageText
    const layoutSvg = await loadCollageSvg(layout, !!showText)
    const photoPositions = parseCollagePhotoPositions(layoutSvg)
    const photos = await matchCollagePhotosToPositions({
      collagePhotos,
      photoPositions,
      layout,
    })

    return {
      layout,
      photos,
      collageText: initialCollageText,
    }
  } catch (e) {
    logger.error('Error initializing collage', e, { layout })
    throw e
  }
}

/**
 * Function receives a collage object and an updater function for a specific photoId in the collage,
 * and returns a new collage object with the updated photo
 */
export function updateCollagePhoto(collage, photoId, updatePhotoFunc) {
  const updatedPhotos = Object.fromEntries(
    Object.entries(collage.photos).map(([id, photo]) => [
      id,
      id === photoId ? updatePhotoFunc({ id, photo }) : photo,
    ])
  )
  return {
    ...collage,
    photos: updatedPhotos,
  }
}

export async function swapCollagePhotos(collage, photos, photoId1, photoId2) {
  const photo1 = photos[photoId1]
  const position1 = photo1.position
  const photo2 = photos[photoId2]
  const position2 = photo2.position
  return {
    ...collage,
    photos: {
      ...collage.photos,
      [photoId1]: await putPhotoInPosition({
        photo: photo2,
        position: position1,
      }),
      [photoId2]: await putPhotoInPosition({
        photo: photo1,
        position: position2,
      }),
    },
  }
}

export async function replaceCollagePhoto(collage, photoId, newPhoto) {
  const photo = collage.photos[photoId]
  const { position } = photo
  return {
    ...collage,
    photos: {
      ...collage.photos,
      [photoId]: await putPhotoInPosition({ photo: newPhoto, position }),
    },
  }
}

export async function removeCollagePhoto(collage, photoId) {
  const photo = collage.photos[photoId]
  const { position } = photo
  return {
    ...collage,
    photos: {
      ...collage.photos,
      [photoId]: await putPhotoInPosition({ photo: null, position }),
    },
  }
}

// Receive "Layout" container and return its "y" value which is the nearest to the bottom
export function getLayoutBottomBound(layoutElementContainer) {
  const getLowestPoint = (element) =>
    Number(element.getAttribute('y')) + Number(element.getAttribute('height'))
  const layoutPhotos = Array.from(layoutElementContainer.children)

  return Math.max(...layoutPhotos.map((element) => getLowestPoint(element)))
}

// Center the text vertically between the "Layout" container and the bottom of collage
export function getTextYPosition(svgElement) {
  const viewBoxHeight = Number(svgElement.getAttribute('viewBox').split(' ')[3])
  const layoutContainerElement = getCollageLayoutContainer(svgElement)
  const layoutBottom = getLayoutBottomBound(layoutContainerElement)
  return viewBoxHeight - (viewBoxHeight - layoutBottom) / 2
}

function createTextElement(collageText, yPosition) {
  const textElement = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'text'
  )
  textElement.setAttribute('x', '50%')
  textElement.setAttribute('y', `${yPosition}`)
  textElement.setAttribute('text-anchor', 'middle')
  textElement.setAttribute('dominant-baseline', 'middle')
  textElement.setAttribute('fill', COLLAGE_FONT_COLOR)
  textElement.setAttribute(
    'font-size',
    `${getFontSize(collageText.text.length)}px`
  )
  textElement.setAttribute(
    'font-family',
    COLLAGE_FONTS[collageText.textStyleId]?.fontFamily
  )
  textElement.textContent = collageText.text
  return textElement
}

/**
 * Generate an <svg> image from a collage
 * @param svgLayout - layout containing { svgContent, textSvgContent } which are the SVG layouts
 * @param photoUrls - mapping of photoId to url of image to place in the collage
 * @param outputImageDimensions - expected width and height of the collage svg
 * @param collageText - tile.collage.text
 * @return svg string represents the collage
 */
export function generateCollageSvg({
  svgLayout,
  photoUrls,
  outputImageDimensions,
  collageText = {},
}) {
  const { width, height } = outputImageDimensions
  const { svgContent, textSvgContent } = svgLayout
  const isText =
    collageText.showText && collageText.text?.length > 0 && textSvgContent
  const collageSvg = parseLayoutSVG({
    svgContent: isText ? textSvgContent : svgContent,
    width,
    height,
    backgroundColor: 'white',
    isText,
  })
  // Add customer photos in addition to the rect elements inside the Layout container
  const layoutContainerElement = getCollageLayoutContainer(collageSvg)
  const layoutPhotos = Array.from(
    layoutContainerElement.querySelectorAll(':scope > *')
  )
    .map((element) => {
      if (element.tagName.toLowerCase() !== 'rect') {
        return element
      }
      const { class: photoId, ...attributes } =
        getHtmlElementAttributes(element) // Classnames define the photo ids in the svg
      const imageElement = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'image'
      )
      Object.entries({ ...attributes, href: photoUrls[photoId] }).forEach(
        ([name, value]) => imageElement.setAttribute(name, value)
      )
      return [imageElement, element]
    })
    .flat()
  layoutContainerElement.replaceChildren(...layoutPhotos)

  if (isText) {
    const yPosition = getTextYPosition(collageSvg)
    const textElement = createTextElement(collageText, yPosition)
    const textPlaceholder = getCollageTextContainer(collageSvg)
    textPlaceholder.replaceChildren(textElement)
  }

  // Shadow is only used for the preview, so we remove it from the final svg
  collageSvg.getElementsByClassName(COLLAGE_SVG_COMPONENTS.SHADOW)[0]?.remove()
  return svgToString(collageSvg)
}

export function getCollageDpi(tileSize) {
  const { width, height } = TILESIZES_TO_INCHES[tileSize]
  const isLargeTile = width * height > 500
  if (isLargeTile) {
    // Return lower DPI for larger sizes because cloudinary takes too much time generating the images and fails
    return 150
  } else {
    return DEFAULT_IMAGE_DPI
  }
}

/**
 * Get the collage photos that are not empty (have a photo)
 * @param collagePhotos
 * @return {*} - object of photoId to photo
 */
export function getNonEmptyCollagePhotos(collagePhotos) {
  return collagePhotos.filter((photo) => !!photo.uid)
}
